import { flexRender, type Row, type Table } from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import { Trash2Icon } from 'lucide-react';
import * as React from 'react';
import { useComposedRefs } from 'src/shared/lib/compose-refs';
import { getRowHeightValue } from 'src/shared/lib/data-grid';
import { cn } from 'src/shared/lib/utils';
import type { CellPosition, RowHeightValue } from 'src/shared/types/data-grid';

interface DataGridRowProps<TData> extends React.ComponentProps<'div'> {
  row: Row<TData>;
  table: Table<TData>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  virtualRowIndex: number;
  rowMapRef: React.RefObject<Map<number, HTMLDivElement>>;
  rowHeight: RowHeightValue;
  focusedCell: CellPosition | null;
}

export const DataGridRow = React.memo(DataGridRowImpl, (prev, next) => {
  if (prev.row.id !== next.row.id) {
    return false;
  }

  const prevRowIndex = prev.virtualRowIndex;
  const nextRowIndex = next.virtualRowIndex;

  const prevHasFocus = prev.focusedCell?.rowIndex === prevRowIndex;
  const nextHasFocus = next.focusedCell?.rowIndex === nextRowIndex;

  if (prevHasFocus !== nextHasFocus) {
    return false;
  }

  if (nextHasFocus && prevHasFocus) {
    const prevFocusedCol = prev.focusedCell?.columnId;
    const nextFocusedCol = next.focusedCell?.columnId;
    if (prevFocusedCol !== nextFocusedCol) {
      return false;
    }
  }

  if (next.rowVirtualizer.isScrolling) {
    return true;
  }

  return false;
}) as typeof DataGridRowImpl;

function DataGridRowImpl<TData>({
  row,
  table,
  virtualRowIndex,
  rowVirtualizer,
  rowMapRef,
  rowHeight,
  focusedCell,
  ref,
  className,
  ...props
}: DataGridRowProps<TData>) {
  const [isHovered, setIsHovered] = React.useState(false);

  const rowRef = useComposedRefs(ref, (node) => {
    if (node && typeof virtualRowIndex !== 'undefined') {
      rowVirtualizer.measureElement(node);
      rowMapRef.current.set(virtualRowIndex, node);
    }
  });

  const isRowSelected = row.getIsSelected();

  const handleDeleteRow = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Check if there's a delete handler in table meta
      const deleteHandler = table.options.meta?.onRowsDelete;
      if (deleteHandler) {
        deleteHandler([row.index]);
      }
    },
    [table.options.meta, row.index]
  );

  return (
    <div
      key={row.id}
      role="row"
      aria-rowindex={virtualRowIndex + 2}
      aria-selected={isRowSelected}
      data-index={virtualRowIndex}
      data-slot="grid-row"
      ref={rowRef}
      tabIndex={-1}
      className={cn(
        'group absolute flex w-full border-b transition-colors',
        isHovered && 'bg-neutral-50 dark:bg-neutral-900/50',
        className
      )}
      style={{
        height: `${getRowHeightValue(rowHeight)}px`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {row.getVisibleCells().map((cell, colIndex) => {
        const isCellFocused = focusedCell?.rowIndex === virtualRowIndex && focusedCell?.columnId === cell.column.id;

        return (
          <div
            key={cell.id}
            role="gridcell"
            aria-colindex={colIndex + 1}
            data-highlighted={isCellFocused ? '' : undefined}
            data-slot="grid-cell"
            tabIndex={-1}
            className={cn({
              'border-r': cell.column.id !== 'select',
            })}
            style={{
              width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
            }}
          >
            {typeof cell.column.columnDef.header === 'function' ? (
              <div
                className={cn('size-full px-3 py-1.5', {
                  'bg-neutral-900/10 dark:bg-neutral-50/10': isRowSelected,
                })}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </div>
        );
      })}

      {/* Floating delete button */}
      {isHovered && table.options.meta?.onRowsDelete && (
        <div className="absolute right-2 top-1/2 z-30 -translate-y-1/2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/90 shadow-sm ring-1 ring-neutral-200 backdrop-blur-sm dark:bg-neutral-800/90 dark:ring-neutral-700">
            <button
              type="button"
              onClick={handleDeleteRow}
              className="flex h-6 w-6 items-center justify-center rounded-sm text-neutral-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:text-neutral-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
              aria-label="Delete row"
            >
              <Trash2Icon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
